// Global
import React from 'react';

// Local
import { Content } from '@generated/Content/FeatureImageAndText.model';
import {
  FeatureImageAndText,
  FeatureCardAlignment,
  FeatureImageSplit,
} from './FeatureImageAndText';
import ImageWrapper from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper, {
  usePlainTextWrapper,
} from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { Themes } from 'lib/themes';
import { SvgImageWrapper } from 'components/helpers/SitecoreWrappers/SvgImageWrapper/SvgImageWrapper';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { Item } from '@sitecore-content-sdk/nextjs';
import { RenderingParamBoolean } from 'lib/types/sitecore';
import { withExternalLinkTarget } from 'lib/utils/link-utils';

type FeatureImageAndTextProps = Content.FeatureImageAndText.FeatureImageAndText_Component & {
  params: {
    featureImageAspectRatio: Item;
    featureContentPosition: FeatureCardAlignment;
    featureImageSplit: FeatureImageSplit;
    featureNoPadding: RenderingParamBoolean;
    /** Set to '1' when this instance is nested under a visible h2 elsewhere on the page and needs to announce as a level-3 heading instead of the default h2. */
    featureHeadlineLevelH3: RenderingParamBoolean;
  };
};

const FeatureImageAndTextWrapper = (props: FeatureImageAndTextProps) => {
  /*
   * Rendering
   */

  // Hooks must be called unconditionally before any early return.
  const headline = usePlainTextWrapper({ field: props.fields?.headline });

  if (!props.fields) {
    return <></>;
  }

  const imageAspectRatio =
    getEnumValue<ImageAspectRatio>(props.params?.featureImageAspectRatio) ?? '3:2';

  // Create a unique ID based on the headline text to allow jump links
  const createFeatureImageAndTextHeadlineId = (headline: string) =>
    `feature-${headline
      .replace(/&/g, 'and')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')}`;

  return (
    <FeatureImageAndText
      contentPosition={props.params?.featureContentPosition as FeatureCardAlignment | undefined}
      headlineId={
        props.fields?.headline?.value
          ? createFeatureImageAndTextHeadlineId(props.fields.headline.value)
          : undefined
      }
      theme={props.params?.theme as Themes}
      imageAspectRatio={imageAspectRatio}
      imageSplit={props.params?.featureImageSplit as FeatureImageSplit}
      noPadding={props.params?.featureNoPadding === '1'}
      headlineLevel={props.params?.featureHeadlineLevelH3 === '1' ? 3 : 2}
      image={<ImageWrapper field={props.fields?.image} />}
      icon={
        props.fields?.icon?.value?.src?.includes('.svg') ? (
          <SvgImageWrapper
            src={props.fields?.icon?.value?.src || ''}
            alt={(props.fields?.icon?.value?.alt as string) || ''}
          />
        ) : (
          <ImageWrapper field={props.fields?.icon} />
        )
      }
      cta1={
        <LinkWrapper
          field={withExternalLinkTarget(props.fields?.cta1)}
          ctaComponentClass={'component-feature-split-color-cta-1-color'}
          ctaStyle="filled"
          contentResultAnalytics={{
            contentListName: props.fields?.headline?.value?.trim() || undefined,
          }}
        />
      }
      cta2={
        <LinkWrapper
          field={withExternalLinkTarget(props.fields?.cta2)}
          ctaComponentClass={'component-feature-split-color-cta-2-color'}
          ctaStyle="outline"
          contentResultAnalytics={{
            contentListName: props.fields?.headline?.value?.trim() || undefined,
          }}
        />
      }
      eyebrow={<PlainTextWrapper field={props.fields?.headlineEyebrow} />}
      headline={headline}
      subheadline={<PlainTextWrapper field={props.fields?.headlineSubhead} />}
      description={<RichTextWrapper field={props.fields?.headlineDescription} />}
    />
  );
};

export const Default = FeatureImageAndTextWrapper;
export const Standard = FeatureImageAndTextWrapper;
